<template>
  <c-input-select
    ref="selectRef"
    v-model="model"
    :label="label"
    :loading="loading"
    use-input
    use-chips
    multiple
    :input-debounce="debounce"
    :options="filterOptions"
    @filter="filter"
  />
</template>

<script setup>
import { ref, useTemplateRef, watch } from "vue";
import { searchIcon, toDuotoneIcon } from "common/defaultIcons.js";

// Props
const props = defineProps({
  label: { type: String, required: true },
  debounce: { type: Number, default: 300 },
  addToMessage: { type: String, default: "toevoegen" },
  searchFunction: { type: Function, required: true },
});

// State
const model = ref([]);
const loading = ref(false);
const filterOptions = ref([]);
const stringOptions = ref([]);

// Refs
const selectRef = useTemplateRef("selectRef");

// Handle new entries in the model
watch(model, ([...newVal]) => {
  const select = selectRef?.value?.selectRef;
  const { updateInputValue } = select || {};

  updateInputValue("");

  const last = newVal.at(-1);
  if (!last) return;

  const isString = typeof last === "string";
  const isCustom = typeof last === "object" && last.__isCustom;

  if (!isString && !isCustom) return;

  model.value.pop(); // Remove last item

  if (isString) {
    // Tried to add a duplicate value so we remove it.
    if (stringOptions.value.includes(last)) {
      updateInputValue("");
      return;
    } else {
      model.value.push({ label: last, value: null });
    }
  } else {
    const label = last.value;
    createValue(label, () => {
      model.value.push({ label, value: null });
    });
    filterOptions.value = [];
  }
});

// Add a new value to internal list and complete callback
const createValue = (val, done) => {
  if (loading.value) {
    return;
  }

  if (val && !stringOptions.value.includes(val)) {
    stringOptions.value.push(val);
  }
  done(val);
};

// Filter both local and remote options
const filter = async (input, update) => {
  filterOptions.value = [];

  update(async () => {
    loading.value = true;
    const needle = input?.toLowerCase() || "";

    const localResults = !needle
      ? [...model.value]
      : stringOptions.value.filter((v) => v.toLowerCase().includes(needle));

    filterOptions.value = [...localResults];

    if (props.searchFunction && needle) {
      try {
        const remoteResults = await props.searchFunction(input);
        if (Array.isArray(remoteResults)) {
          const unique = new Set([...filterOptions.value, ...remoteResults]);
          filterOptions.value = [...unique];
        }
      } catch (err) {
        console.error("Search error:", err);
      }
    }

    // Check if input already exists
    const exists = filterOptions.value.some((opt) => {
      if (typeof opt === "string") return opt.toLowerCase() === needle;
      return (
        opt?.label?.toLowerCase?.() === needle ||
        opt?.value?.toLowerCase?.() === needle
      );
    });

    loading.value = false;

    // Add custom entry if not already present
    if (needle && !exists && !loading.value) {
      filterOptions.value.push({
        label: `"${input}" ${props.addToMessage}`,
        value: input,
        icon: toDuotoneIcon(searchIcon),
        __isCustom: true,
      });
    }
  });
};
</script>
